import { PAYMENT_TYPE } from "constants/paymentType";
import { useCreateUnprocessedOrderConfirmModal } from "features/useCreateUnprocessedOrderConfirmModal";
import { useMemo } from "react";
import { isPayPalPendingError } from "utility/paypal";

import { PAYMENT_STATUS } from "../../constants/PaymentStatus";
import { resolveDeliveryShippingUniqueId } from "../../utils/resolveDeliveryShippingUniqueId";
import { toDeliveryData } from "../../utils/toDeliveryData";
import { useEventImages } from "../useEventImages";
import { useOrderAddress } from "../useOrderAddress";
import { useOrderData } from "../useOrderData";
import { useOrderFulfillment } from "../useOrderFulfillment";
import { useOrderList } from "../useOrderList";
import { useOrderTransaction } from "../useOrderTransaction";
import { useTicketBonusMap } from "../useTicketBonusMap";

import type { OrderDetailSource } from "../../types/orderDetailSource";
import type { TicketIdsByEvent } from "../useTicketBonusMap";
import type { ActiveTransaction } from "@spwn/types/firebase/firestore";

type Input = {
  /** falseの間は取得を始めない。取得元の切り替え中に二重取得しないため */
  enabled: boolean;
  orderId: string;
};

/** 注文に含まれるチケットをイベントごとにまとめる。特典メディアの有無をイベント単位で問い合わせるため */
const groupTicketIdsByEvent = (
  transaction: ActiveTransaction
): TicketIdsByEvent[] => {
  const ticketIdsMap = new Map<string, string[]>();
  transaction.products?.forEach((product) => {
    if (product.productType !== "ticket") return;
    ticketIdsMap.set(product.eid, [
      ...(ticketIdsMap.get(product.eid) ?? []),
      product.ref.id,
    ]);
  });
  return Array.from(ticketIdsMap.entries()).map(([eventId, ticketIds]) => ({
    eventId,
    ticketIds,
  }));
};

/** Firestoreの注文ドキュメントをRedux経由で直読みする従来の取得元 */
export const useOrderDetailFromFirestore = (
  input: Input
): OrderDetailSource => {
  const { enabled, orderId } = input;
  const { transaction, isInvalidTransaction } = useOrderTransaction({
    enabled,
    orderId,
  });

  const {
    fulfillmentData,
    shippingUniqueId: fulfillmentShippingUniqueId,
    isLoading: isLoadingFulfillment,
    isError: isFulfillmentError,
  } = useOrderFulfillment({
    fulfillmentFirestoreId: transaction?.fulfillmentRefs?.[0]?.id ?? null,
    orderId: transaction?.orderId?.toString() ?? null,
  });
  const { addressData, isLoading: isLoadingAddressData } = useOrderAddress({
    shippingUniqueId: resolveDeliveryShippingUniqueId({
      orderShippingUniqueId: transaction?.shippingUniqueId ?? null,
      fulfillmentShippingUniqueId,
      isLoadingFulfillment,
      isFulfillmentError,
    }),
  });
  // 出庫の取得を待つ間も住所は読み込み中として扱う
  const isLoadingAddress = isLoadingFulfillment || isLoadingAddressData;
  const {
    eventImageMap,
    eventPartsMap,
    isLoading: isImageLoading,
  } = useEventImages({
    transaction,
  });
  const ticketsByEvent = useMemo(() => {
    if (!transaction) return null;
    return groupTicketIdsByEvent(transaction);
  }, [transaction]);
  const { bonusMap, isLoadingBonus } = useTicketBonusMap({
    orderId: transaction?.orderId?.toString() ?? null,
    ticketsByEvent,
  });

  const { orderData } = useOrderData({ transaction });
  const { orderList } = useOrderList({
    transaction,
    eventImageMap,
    eventPartsMap,
    bonusMap,
    isImageLoading,
  });

  const isPurchased =
    orderData !== null && orderData.paymentStatus === PAYMENT_STATUS.PAYSUCCESS;

  const isPayPalPending =
    transaction?.payType === PAYMENT_TYPE.PayPal &&
    transaction.status === PAYMENT_STATUS.UNPROCESS &&
    isPayPalPendingError(transaction.errorInfo ?? null);

  const showPaymentAlert =
    !isPurchased && transaction?.status === PAYMENT_STATUS.UNPROCESS;

  const deliveryData = useMemo(() => {
    if (!addressData) return null;
    return toDeliveryData(addressData);
  }, [addressData]);

  const { createConfirmCvsPaymentData, renderConfirmCvsPayment } =
    useCreateUnprocessedOrderConfirmModal();

  const handleContinuePayment = () => {
    if (transaction) {
      createConfirmCvsPaymentData(transaction);
    }
  };

  return {
    deliveryData,
    fulfillmentData,
    handleContinuePayment,
    isFetchError: false,
    isInvalid: isInvalidTransaction,
    isLoadingAddress,
    isLoadingBonus,
    isLoadingFulfillment,
    isPayPalPending,
    isPurchased,
    orderData,
    orderList,
    renderConfirmCvsPayment,
    showPaymentAlert,
  };
};
